/**
 * The verifier portal's spec: the whole of what that workspace is, since it
 * has no model and no controller of its own.
 *
 * It borrows every screen it shows — the inbox from `verifications/`, the
 * feed from `notifications/`, the account from `profile/` — and contributes
 * only the frame `shell.model.ts` draws around them. Most of a portal is
 * absent on purpose, and the docblock on the spec argues each omission.
 */

import { definePortal } from "../shell/portal.ts";

/**
 * The verification workspace: the smallest portal ONDO has, on purpose.
 *
 * WHO OPENS IT. Somebody with nothing else to be. `ondo.principal_capacities()`
 * answers `verifier` only when its three other branches yield nothing — so a
 * collector who is asked to confirm something reads it inside their own portal
 * and never sees this one. The account that lands here is the haulier, the
 * registrar, the conservator's assistant: a party who was asked to put their
 * name to one line and has no holdings, no mandate and no membership.
 *
 * WHAT IS DELIBERATELY ABSENT, and it is most of a portal. There is no
 * dashboard, because there is nothing to summarise; no collection, because they
 * hold none; no work record, because `ondo.asked_provenance_entry_ids()` gives
 * them THE ENTRY AND STOPS — a work-record screen here would be a screen that
 * could only ever say "not found". `recordFallback` is the inbox for the same
 * reason: there is no record to fall back to.
 *
 * The rail is three items and one of them is the whole product. That is not a
 * portal waiting to be filled in — a party asked one question should not be
 * handed a workspace implying they have a practice here.
 */
export type VerifierDestination =
  | "verifications"
  | "notifications"
  | "notification-preferences"
  | "about"
  | "activity"
  | "profile";

export const VERIFIER_COPY = {
  eyebrow: "Verification",
  navLabel: "Verification workspace",
} as const;

export const VERIFIER_PORTAL = definePortal<VerifierDestination>({
  eyebrow: VERIFIER_COPY.eyebrow,
  navLabel: VERIFIER_COPY.navLabel,

  destinations: [
    { id: "verifications", label: "Your verifications", glyph: "✓", state: "ready", group: "main" },
    { id: "notifications", label: "Notifications", glyph: "⊘", state: "ready", group: "foot" },
    { id: "notification-preferences", label: "Notification Settings", glyph: "⚙", state: "ready", group: "foot" },
    { id: "about", label: "About ONDO", glyph: "i", state: "ready", group: "foot" },
    { id: "activity", label: "Activity & access log", glyph: "≡", state: "ready", group: "foot" },
    { id: "profile", label: "My Profile", glyph: "◉", state: "ready", group: "foot" },
  ],

  headers: {
    verifications: {
      title: "Your verifications",
      // "You", not the subject's name — and this is the one portal where that is
      // right. The agent's says the firm's name because a member reads a screen
      // about the FIRM's mandates; here the workspace is only ever personal
      // (nothing derives a verifier capacity for an organisation onto this
      // screen), and the request was addressed to this reader by name.
      subtitle:
        "Someone has asked you to confirm information they have recorded on ONDO. " +
        "You can see what you were asked about, and nothing else.",
    },
    notifications: {
      title: "Notifications",
      subtitle: "What has been sent to you",
    },
    "notification-preferences": {
      title: "Notification Settings",
      subtitle:
        "How each kind of notification reaches you — immediately, in the daily digest, or not at all",
    },
    about: { title: "About ONDO", subtitle: "" },
    activity: {
      title: "Activity & access log",
      // OND-68. The second clause is new and true: reads are recorded, on the
      // access trail. UNHEDGED, unlike the gallery's, agent's and estate's —
      // and the difference is not an oversight. A verifier workspace is only
      // ever personal (see `membership` in `VerifierWorkspace`), so there is no
      // firm whose trail this reader could administer and no clause about one
      // to make. It reads like the collector's for the same reason.
      subtitle: "What you have done on ONDO, and every record you have opened",
    },
    profile: {
      title: "My Profile",
      subtitle: "Your ONDO account",
    },
  },

  // Nothing is `planned` here, so nothing needs a page explaining what is
  // missing. The rail lists what exists and stops.
  notYet: {},

  defaultDestination: "verifications",

  records: {
    verifications: "none",
    notifications: "none",
    "notification-preferences": "none",
    about: "none",
    activity: "none",
    profile: "none",
  },

  // There is no work record in this portal, so nothing can ask for a fallback.
  // It is the inbox because `recordFallback` must name a destination that
  // exists, not because anything routes here.
  recordFallback: "verifications",

  badges: { verifications: "verifications", notifications: "notifications", settlements: null },

  profileAt: "profile",

  // No greeting. The front page is a list of things somebody else needs from
  // this reader, and "Welcome back" over an obligation is the wrong register —
  // the agent portal declines it for the same reason.
  greetsAt: null,

  // No header action, and this is the decision the portal turns on. The design
  // this was drawn from has a "+ Request a verification" button, which this
  // account cannot honour: `ondo.request_provenance_verification()` opens with
  // `ondo.can_curate_provenance_entry()`, and a party who holds no work curates
  // no entry. A button that refused every time would be worse than its absence.
  primaryAction: () => null,
  secondaryAction: () => null,
});
